import { isSameDay } from 'date-fns'
import React from 'react'
import { EvenColumns } from '@opallabs/kuiper'
import { iterateDays } from '@opallabs/lambda'
import { CreateButton } from '../../CreateButton'
import { isWithinStoryDateRange } from '../../helpers'
import { WeekViewCategory, WeekViewMode, WeekViewSortType } from '../../types'
import { formatCategoryKey } from '../../useMomentMap/momentMap'
import { WeekViewGlobalContext } from '../../weekViewGlobalContext'
import { useWeekViewSettings } from '../../WeekViewSettingsContext'
import * as styles from './styles'

// which column index to place the "No Moments" badge
const TEXT_COLUMN_INDEX = 3
const NUMBER_OF_DAYS = 7

type DayPlusButtonProps = {
  category: WeekViewCategory
  date: Date
  onBlur: () => void
}

const DayPlusButton: React.VFC<DayPlusButtonProps> = ({
  category,
  date,
  onBlur,
}) => {
  const { onAddMomentCreator, setDisableActiveColumn, story } =
    React.useContext(WeekViewGlobalContext)
  const { selectedSort, viewMode } = useWeekViewSettings()
  const isContentMode = viewMode === WeekViewMode.Content

  let message = null
  let disableButton = false

  if (!isWithinStoryDateRange(date, story)) {
    message = 'Outside of story date range'
    disableButton = true
  }

  // Moment creation requires Time-of-Day sort (categories → day_segment).
  // Content creation doesn't have that restriction, so skip this gate in
  // Content mode.
  if (!isContentMode && selectedSort.type !== WeekViewSortType.Time) {
    message = 'Sort by time to create Moment'
    disableButton = true
  }

  const onClickAdd = () => {
    setDisableActiveColumn(true)
    onAddMomentCreator(category, date)
  }

  return (
    <CreateButton
      disabled={disableButton}
      onBlur={onBlur}
      onClickAdd={onClickAdd}
      tooltipMessage={message}
    />
  )
}

type CategoryDisplayProps = {
  category: WeekViewCategory
}

export const CategoryDisplay: React.VFC<CategoryDisplayProps> = ({
  category,
}) => {
  return (
    <span className={`category-title ${formatCategoryKey(category)}`}>
      {category.title}
    </span>
  )
}

type DayProps = {
  category: WeekViewCategory
  date: Date
  displayCategory: boolean
  isActiveColumn: boolean
  setActiveColumn: React.Dispatch<
    React.SetStateAction<{ category: string | null; date: Date | null }>
  >
}

const Day: React.VFC<DayProps> = ({
  category,
  date,
  displayCategory,
  isActiveColumn,
  setActiveColumn,
}) => {
  const categoryDisplay = displayCategory ? (
    <CategoryDisplay category={category} />
  ) : null

  const showCreator = () => {
    setActiveColumn({ date, category: category.title })
  }

  const hideCreator = () => {
    setActiveColumn({ date: null, category: null })
  }

  /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
  return (
    <div
      css={styles.day}
      onMouseEnter={showCreator}
      onMouseLeave={hideCreator}
      onFocus={showCreator}
      tabIndex={0}
    >
      {isActiveColumn ? (
        <DayPlusButton category={category} date={date} onBlur={hideCreator} />
      ) : (
        categoryDisplay
      )}
    </div>
  )
  /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
}

type CategoryHeaderProps = {
  category: WeekViewCategory
}

export const CategoryHeader: React.VFC<CategoryHeaderProps> = ({
  category,
}) => {
  const {
    activeColumn,
    disableMomentCreator,
    setActiveColumn,
    weekViewStartDate,
  } = React.useContext(WeekViewGlobalContext)

  if (disableMomentCreator) {
    return (
      <div css={styles.categoryHeader}>
        <CategoryDisplay category={category} />
      </div>
    )
  }

  return (
    <div className="category-header" css={styles.categoryHeader}>
      <EvenColumns styleOverrides={{ height: '100%', width: '100%' }}>
        {iterateDays(weekViewStartDate, NUMBER_OF_DAYS, (date, columnIndex) => {
          const isActiveColumn =
            isSameDay(date, activeColumn.date) &&
            activeColumn.category === category.title

          return (
            <Day
              category={category}
              date={date}
              displayCategory={columnIndex === TEXT_COLUMN_INDEX}
              isActiveColumn={isActiveColumn}
              key={date.toISOString()}
              setActiveColumn={setActiveColumn}
            />
          )
        })}
      </EvenColumns>
    </div>
  )
}
